if (!customElements.get('sticky-product-atc')) {
  customElements.define(
    'sticky-product-atc',
    class StickyProductAtc extends HTMLElement {
      connectedCallback() {
        this.proxyButton = this.querySelector('.product-sticky-atc__button');
        this.proxyText = this.proxyButton?.querySelector('span');
        this.productInfo = this.closest('product-info');
        this.sourceButtonId = this.dataset.sourceButton;
        this.frame = null;
        this.stickyTitle = this.querySelector('.product-sticky-atc__title');
        this.stickyImage = this.querySelector('.product-sticky-atc__image');
        this.cookieWidgetOriginalStyles = null;

        if (!this.proxyButton || !this.sourceButtonId) return;

        this.onProxyClick = this.onProxyClick.bind(this);
        this.onViewportChange = this.onViewportChange.bind(this);
        this.proxyButton.addEventListener('click', this.onProxyClick);
        window.addEventListener('scroll', this.onViewportChange, { passive: true });
        window.addEventListener('resize', this.onViewportChange, { passive: true });

        this.resolveSourceButton();

        this.productObserver = new MutationObserver(() => {
          this.resolveSourceButton();
          this.syncButton();
          this.onViewportChange();
        });
        this.productObserver.observe(
          this.productInfo?.querySelector('.product') || this.productInfo || document.body,
          {
            attributes: true,
            childList: true,
            subtree: true,
            characterData: true,
          }
        );

        this.cookieHostObserver = new MutationObserver(() => this.observeCookieWidget());
        this.cookieHostObserver.observe(document.body, { childList: true, subtree: true });
        this.observeCookieWidget();

        this.menuDrawer = document.getElementById('Details-menu-drawer-container');
        this.navigationObserver = new MutationObserver(() => this.onViewportChange());
        if (this.menuDrawer) {
          this.navigationObserver.observe(this.menuDrawer, {
            attributes: true,
            attributeFilter: ['open', 'class'],
          });
        }
        this.navigationObserver.observe(document.body, {
          attributes: true,
          attributeFilter: ['class'],
        });

        this.resizeObserver = new ResizeObserver(() => this.updateOffset());
        this.resizeObserver.observe(this);

        this.syncButton();
        this.onViewportChange();
      }

      disconnectedCallback() {
        this.proxyButton?.removeEventListener('click', this.onProxyClick);
        window.removeEventListener('scroll', this.onViewportChange);
        window.removeEventListener('resize', this.onViewportChange);
        this.productObserver?.disconnect();
        this.cookieHostObserver?.disconnect();
        this.cookieShadowObserver?.disconnect();
        this.navigationObserver?.disconnect();
        this.resizeObserver?.disconnect();
        this.setVisible(false);
      }

      resolveSourceButton() {
        const currentButton = document.getElementById(this.sourceButtonId);
        if (currentButton === this.sourceButton) return;
        this.sourceButton = currentButton;
      }

      syncButton() {
        if (!this.sourceButton) return;

        const sourceText = this.sourceButton.querySelector('span:not(.sold-out-message)');
        const soldOutText = this.sourceButton.querySelector('.sold-out-message:not(.hidden)');
        const label = (soldOutText || sourceText)?.textContent?.trim();
        if (label && this.proxyText) this.proxyText.textContent = label;

        const disabled =
          this.sourceButton.disabled || this.sourceButton.getAttribute('aria-disabled') === 'true';
        this.proxyButton.disabled = disabled;
        this.proxyButton.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        this.proxyButton.classList.toggle('loading', this.sourceButton.classList.contains('loading'));
        this.querySelector('.loading__spinner')?.classList.toggle(
          'hidden',
          !this.sourceButton.classList.contains('loading')
        );

        const currentTitle = this.productInfo?.querySelector('[data-product-variant-title]')?.textContent?.trim();
        if (currentTitle && this.stickyTitle) this.stickyTitle.textContent = currentTitle;

        const currentImage = this.productInfo?.querySelector(
          '.product__media-item.is-active img, .product__media-item[aria-hidden="false"] img'
        );
        if (currentImage && this.stickyImage) {
          this.stickyImage.removeAttribute('srcset');
          this.stickyImage.src = currentImage.currentSrc || currentImage.src;
          this.stickyImage.alt = currentImage.alt || currentTitle || '';
        }
      }

      onProxyClick() {
        this.resolveSourceButton();
        this.syncButton();
        if (!this.sourceButton || this.proxyButton.disabled) return;
        this.sourceButton.click();
      }

      onViewportChange() {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.resolveSourceButton();
          if (!this.sourceButton) {
            this.setVisible(false);
            return;
          }

          const sourceRect = this.sourceButton.getBoundingClientRect();
          this.setVisible(sourceRect.bottom < 0 && !this.isMobileNavigationOpen());
        });
      }

      isMobileNavigationOpen() {
        if (!window.matchMedia('(max-width: 989px)').matches) return false;

        return Boolean(
          this.menuDrawer?.hasAttribute('open') || document.body.classList.contains('overflow-hidden-tablet')
        );
      }

      setVisible(visible) {
        if (this.visible === visible) return;
        this.visible = visible;
        this.classList.toggle('is-visible', visible);
        this.toggleAttribute('aria-hidden', !visible);
        document.body.classList.toggle('product-sticky-atc-visible', visible);
        this.updateOffset();
        this.updateCookieWidget(visible);
      }

      updateOffset() {
        if (!this.visible) return;
        const styles = window.getComputedStyle(this);
        const bottom = Number.parseFloat(styles.bottom) || 0;
        const offset = Math.ceil(this.getBoundingClientRect().height + bottom + 8);
        document.documentElement.style.setProperty('--product-sticky-atc-offset', `${offset}px`);
        this.updateCookieWidget(true);
      }

      observeCookieWidget() {
        const host = document.querySelector('pandectes-cmp');
        if (!host?.shadowRoot) return;

        if (host !== this.cookieHost) {
          this.cookieShadowObserver?.disconnect();
          this.cookieHost = host;
          this.cookieShadowObserver = new MutationObserver(() => this.updateCookieWidget(this.visible));
          this.cookieShadowObserver.observe(host.shadowRoot, { childList: true, subtree: true });
        }

        this.updateCookieWidget(this.visible);
      }

      updateCookieWidget(visible) {
        const cookieWidget = this.cookieHost?.shadowRoot?.querySelector(
          'button.widget.bottom.left, button[aria-label*="consim" i], button[aria-label*="consent" i]'
        );
        if (!cookieWidget) return;

        if (cookieWidget !== this.cookieWidget) {
          if (this.cookieWidget) this.restoreCookieWidget();
          this.cookieWidget = cookieWidget;
          this.cookieWidgetOriginalStyles = {
            transform: {
              value: cookieWidget.style.getPropertyValue('transform'),
              priority: cookieWidget.style.getPropertyPriority('transform'),
            },
            transition: {
              value: cookieWidget.style.getPropertyValue('transition'),
              priority: cookieWidget.style.getPropertyPriority('transition'),
            },
          };
        }

        if (!visible) {
          this.restoreCookieWidget();
          return;
        }

        cookieWidget.style.setProperty(
          'transform',
          'translateY(calc(-1 * var(--product-sticky-atc-offset, 10rem)))',
          'important'
        );
        cookieWidget.style.setProperty('transition', 'transform 220ms ease', 'important');
      }

      restoreCookieWidget() {
        if (!this.cookieWidget || !this.cookieWidgetOriginalStyles) return;

        const restoreProperty = (property, original) => {
          if (original.value) this.cookieWidget.style.setProperty(property, original.value, original.priority);
          else this.cookieWidget.style.removeProperty(property);
        };
        restoreProperty('transform', this.cookieWidgetOriginalStyles.transform);
        restoreProperty('transition', this.cookieWidgetOriginalStyles.transition);
      }

    }
  );
}
